/**
 * Возврат из ЮKassa.
 *
 * Экран существует ради одного факта, который легко проглядеть:
 * **возврат клиента оплату не подтверждает**. Деньги подтверждает вебхук
 * провайдера или наша сверка, а на адрес возврата можно прийти и руками. Значит
 * «Спасибо, оплачено!» по факту возврата — это ложь, которую продукт скажет
 * раньше, чем узнает правду.
 *
 * Отсюда устройство:
 *
 * * статус всегда перечитывается запросом `GET /billing/payments/{id}`;
 * * частоту опроса задаёт сервер заголовком `Retry-After`, а в терминальном
 *   ответе его нет — это и есть команда остановиться (docs/API.md §8);
 * * состояние «платёж прошёл, подтверждение ещё не дошло» — **обычное**, а не
 *   ошибка, и называется словами: деньги списаны, ждём подтверждения, подписка
 *   включится сама;
 * * идентификатор платежа берётся из адреса, из памяти вкладки или из списка
 *   последних платежей — в таком порядке. Клиент, вернувшийся в другой вкладке
 *   или с закрытым хранилищем, всё равно видит свой платёж, а не пустоту.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { KvRow, Kv } from '../ui/form';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import {
  forgetPayment,
  getPayment,
  listPayments,
  rememberedPayment,
  type PaymentDetail,
} from '../api/billing';
import { dateProse, moneyKopecks, momentProse } from '@shared/src/format';

/** Запасной интервал опроса, если сервер не прислал свой. */
const POLL_FALLBACK = 3;

type Load =
  | { kind: 'looking' }
  | { kind: 'nothing' }
  | { kind: 'ready'; payment: PaymentDetail; waiting: boolean }
  | { kind: 'failed'; error: unknown };

/** Терминальные состояния: дальше опрашивать нечего. */
const TERMINAL = new Set(['succeeded', 'canceled', 'refunded', 'partially_refunded']);

export function PaymentReturn() {
  const [params] = useSearchParams();
  const [load, setLoad] = useState<Load>({ kind: 'looking' });

  useEffect(() => {
    const abort = new AbortController();
    let timer: number | undefined;

    /**
     * Найти платёж: адрес → память вкладки → последний платёж организации.
     *
     * Первый источник — единственный точный, и теперь он есть: `payment_id`
     * дописывает сервер в тот адрес возврата, который уезжает провайдеру.
     * Раньше его не было вовсе, и на брошенной оплате два оставшихся источника
     * уверенно показывали ЧУЖОЙ платёж.
     */
    async function findId(): Promise<string | null> {
      const fromUrl = params.get('payment_id');
      if (fromUrl) return fromUrl;
      const stored = rememberedPayment();
      if (stored) return stored;
      const page = await listPayments(abort.signal);
      return page.items[0]?.id ?? null;
    }

    const poll = (id: string) => {
      getPayment(id, abort.signal)
        .then(({ body, retryAfter }) => {
          const terminal = TERMINAL.has(body.status);
          setLoad({ kind: 'ready', payment: body, waiting: !terminal });
          if (!terminal) {
            timer = window.setTimeout(
              () => poll(id),
              (retryAfter ?? POLL_FALLBACK) * 1000,
            );
          } else {
            forgetPayment();
          }
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setLoad({ kind: 'failed', error });
        });
    };

    findId()
      .then((id) => (id ? poll(id) : setLoad({ kind: 'nothing' })))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });

    return () => {
      abort.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [params]);

  const head = (
    <div className="screen-head">
      <h1>Платёж</h1>
      <Link className="btn btn-ghost btn-sm" to="/plan">
        К тарифу
      </Link>
    </div>
  );

  if (load.kind === 'looking') {
    return (
      <>
        {head}
        <Skeleton rows={2} />
      </>
    );
  }

  if (load.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice error={load.error} />
        <p className="prose-note">
          Состояние платежа мы всё равно узнаем: всё, что дольше десяти минут висит без
          ответа, мы перечитываем у банка сами. Если деньги списались, подписка включится
          без вашего участия.
        </p>
      </>
    );
  }

  if (load.kind === 'nothing') {
    return (
      <>
        {head}
        <Empty
          eyebrow="Платежа не нашлось"
          title="Мы не видим начатого платежа"
          action={
            <Link className="btn btn-primary" to="/plan/checkout">
              Оформить подписку
            </Link>
          }
        >
          Возможно, оплата открывалась в другом браузере или страница открыта по прямой
          ссылке. Все платежи организации видны на экране тарифа — там же будет и этот,
          как только банк о нём сообщит.
        </Empty>
      </>
    );
  }

  const { payment, waiting } = load;
  // Чеков у платежа бывает несколько (приход и возврат), и у каждого свой
  // фискальный номер; на этом экране показывается чек прихода.
  const receipt = (payment.receipts ?? []).find((r) => r.kind === 'payment');

  return (
    <>
      {head}

      {payment.status === 'succeeded' ? (
        <section className="summary">
          <div>
            <div className="value">Оплачено</div>
            <div className="status">Подписка активна</div>
          </div>
          <div>
            <div className="h3 tnum">{moneyKopecks(payment.amount_kopecks)}</div>
            <div className="stamp">
              {payment.captured_at ? momentProse(payment.captured_at) : ''}
            </div>
          </div>
          <p className="stamp basis">
            {payment.description ?? 'Подписка Seeno'} · платёж подтверждён провайдером ·
            чек уйдёт на почту организации
          </p>
        </section>
      ) : null}

      {payment.status === 'canceled' ? (
        <div className="notice" role="alert">
          <b>Платёж не прошёл</b>
          {payment.cancellation_reason ??
            'Банк отклонил операцию. На нашей стороне отказа не было, деньги не списаны.'}{' '}
          Подписка не включилась, замеры идут в прежнем режиме. Попробуйте другую карту
          или выставьте счёт на юрлицо.
          <span className="notice-act">
            <Link className="btn btn-primary btn-sm" to="/plan/checkout">
              Попробовать ещё раз
            </Link>
          </span>
        </div>
      ) : null}

      {waiting ? (
        <div className="notice warn" role="status" aria-live="polite">
          <b>
            {payment.status === 'waiting_for_capture'
              ? 'Банк удерживает деньги, ждём подтверждения'
              : 'Платёж создан, ждём подтверждения'}
          </b>
          Возврат из платёжной страницы сам по себе оплату не подтверждает: подтверждение
          приходит от банка отдельным уведомлением, обычно за несколько секунд. Мы
          обновляем это состояние сами — страницу перезагружать не нужно.
          <span className="stamp">
            Если уведомление потеряется, мы перечитаем платёж у банка сами и подписка
            включится без вашего участия. Закрывать страницу можно.
          </span>
        </div>
      ) : null}

      <Kv>
        <KvRow label="Сумма">{moneyKopecks(payment.amount_kopecks)}</KvRow>
        <KvRow label="Назначение">{payment.description ?? 'Подписка Seeno'}</KvRow>
        <KvRow label="Создан">{momentProse(payment.created_at)}</KvRow>
        <KvRow label="Способ">
          {payment.provider === 'invoice' ? 'Счёт на юрлицо' : 'ЮKassa'}
        </KvRow>
        {receipt ? (
          <KvRow
            label="Фискальный чек"
            note={
              receipt.registered_at
                ? `зарегистрирован ${dateProse(receipt.registered_at)}`
                : undefined
            }
          >
            № {receipt.fiscal_document_number ?? '—'}
          </KvRow>
        ) : null}
      </Kv>

      {payment.status === 'succeeded' ? (
        <div className="row-actions">
          <Link className="btn btn-primary" to="/">
            Перейти к замеру
          </Link>
          <Link className="btn btn-ghost" to="/plan">
            Тариф и документы
          </Link>
        </div>
      ) : null}
    </>
  );
}
